import { useQuery } from '@tanstack/react-query'
import { getWelcomeCoupon } from './api/booking'
import { isNativePlatform } from './platform'
import { useAuth } from '../store/authStore'
import { useBooking } from '../store/bookingStore'

/**
 * Whether this account may use the welcome discount.
 *
 * Reports only — it deliberately never touches the cart. Applying a discount the customer did not
 * ask for is confusing and hard to undo; they copy the code and apply it themselves, and can
 * remove it again like any other coupon.
 *
 * Only ever queried from the installed app and only while signed in, so the offer does not appear
 * on the web. That is a marketing boundary, not a security one: the backend re-decides entitlement
 * on every price calculation and on the booking itself.
 */
export function useWelcomeCoupon() {
  const user = useAuth((s) => s.user)
  const couponCode = useBooking((s) => s.cart.couponCode)

  const enabled = isNativePlatform() && Boolean(user)

  const { data } = useQuery({
    queryKey: ['welcomeCoupon', user?.email ?? null],
    queryFn: getWelcomeCoupon,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const eligible = Boolean(enabled && data?.eligible && data.code)

  return {
    eligible,
    /** True once the customer has applied it themselves. */
    applied: Boolean(data?.code) && couponCode === data?.code,
    discount: data?.discount ?? null,
  }
}
